import { useEffect, useMemo, useState } from "react";
import { useForm, type SubmitHandler, type UseFormSetValue, type UseFormWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { TitleCaseInput } from "@/components/ui/text-input";
import { LocationPicker } from "@/components/LocationPicker";
import { sitioCreateSchema, tipoSitioEnum, TIPO_SITIO_LABEL, type SitioCreateInput, type Sitio } from "./schemas";
import { useCreateSitio, useUpdateSitio } from "./api";
import type { ApiError } from "@/lib/api";

const DEFAULTS: SitioCreateInput = {
  nombre: "",
  tipo: "wireless_tower",
  direccion: "",
  municipio: "",
  estado: "",
  latitud: "",
  longitud: "",
  altura_metros: "",
  notas: "",
  tags: [],
};

function toForm(s: Sitio): SitioCreateInput {
  return {
    nombre: s.nombre,
    tipo: s.tipo,
    direccion: s.direccion ?? "",
    municipio: s.municipio ?? "",
    estado: s.estado ?? "",
    latitud: s.latitud ? Number(s.latitud) : "",
    longitud: s.longitud ? Number(s.longitud) : "",
    altura_metros: s.altura_metros ?? "",
    notas: s.notas ?? "",
    tags: s.tags ?? [],
  };
}

function clean<T>(v: T): T {
  if (typeof v === "string") return (v.trim() === "" ? undefined : v.trim()) as T;
  if (Array.isArray(v)) return v.map(clean) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      const c = clean(val);
      if (c !== undefined) out[k] = c;
    }
    return out as T;
  }
  return v;
}

function SitioLocationMap({
  watch,
  setValue,
}: {
  watch: UseFormWatch<SitioCreateInput>;
  setValue: UseFormSetValue<SitioCreateInput>;
}) {
  const lat = watch("latitud");
  const lng = watch("longitud");
  const value = useMemo(() => {
    const latNum = lat === undefined || lat === null || lat === "" ? null : Number(lat);
    const lngNum = lng === undefined || lng === null || lng === "" ? null : Number(lng);
    if (latNum === null || lngNum === null || isNaN(latNum) || isNaN(lngNum)) return null;
    return { lat: latNum, lng: lngNum };
  }, [lat, lng]);
  return (
    <LocationPicker
      value={value}
      onChange={(loc) => {
        setValue("latitud", Number(loc.lat.toFixed(7)), { shouldDirty: true, shouldValidate: true });
        setValue("longitud", Number(loc.lng.toFixed(7)), { shouldDirty: true, shouldValidate: true });
      }}
    />
  );
}

export function SitioFormDialog({
  open, onOpenChange, sitio, onSaved,
}: { open: boolean; onOpenChange: (v: boolean) => void; sitio?: Sitio | null; onSaved?: (id: string) => void }) {
  const editMode = !!sitio;
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm<SitioCreateInput>({
    resolver: zodResolver(sitioCreateSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) { reset(sitio ? toForm(sitio) : DEFAULTS); setError(null); }
  }, [open, sitio, reset]);

  const createMut = useCreateSitio();
  const updateMut = useUpdateSitio(sitio?.id ?? "");

  const onSubmit: SubmitHandler<SitioCreateInput> = async (values) => {
    setError(null);
    try {
      const cleaned = clean(values);
      const saved = editMode
        ? await updateMut.mutateAsync(cleaned)
        : await createMut.mutateAsync(cleaned);
      onSaved?.(saved.id);
      onOpenChange(false);
    } catch (e) { setError((e as ApiError).message ?? "No se pudo guardar"); }
  };

  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle>{editMode ? "Editar sitio" : "Nuevo sitio"}</DialogTitle>
          <DialogDescription>Ubicación física para contratos y routers (torre, OLT, nodo).</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <TitleCaseInput control={control} name="nombre" placeholder="Torre Centro" />
              {errors.nombre && <span className="text-xs text-destructive">{errors.nombre.message}</span>}
            </div>
            <div>
              <Label>Tipo</Label>
              <Select {...register("tipo")}>
                {tipoSitioEnum.map((t) => <option key={t} value={t}>{TIPO_SITIO_LABEL[t]}</option>)}
              </Select>
            </div>
            <div>
              <Label>Altura (m)</Label>
              <Input type="number" {...register("altura_metros")} />
            </div>
            <div className="col-span-2">
              <Label>Dirección</Label>
              <TitleCaseInput control={control} name="direccion" />
            </div>
            <div>
              <Label>Municipio</Label>
              <TitleCaseInput control={control} name="municipio" />
            </div>
            <div>
              <Label>Estado</Label>
              <TitleCaseInput control={control} name="estado" />
            </div>
            <div className="col-span-2">
              <Label>Ubicación en mapa</Label>
              <SitioLocationMap watch={watch} setValue={setValue} />
              <div className="mt-1 grid grid-cols-2 gap-2">
                <Input type="number" step="0.0000001" placeholder="Latitud" {...register("latitud")} />
                <Input type="number" step="0.0000001" placeholder="Longitud" {...register("longitud")} />
              </div>
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea rows={2} {...register("notas")} />
            </div>
          </div>
          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Guardando…" : editMode ? "Guardar cambios" : "Crear sitio"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
